import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { api, type ClipView } from "../api";
import { VIEW_PRESETS, isCalibrated, type CalibratedLens, type Lens, type Pose } from "../fisheye/lens";
import type { Layout } from "../fisheye/renderer";
import { FisheyeView } from "./FisheyeView";

/** How to dewarp a camera's picture: the fisheye itself (free look-around,
 *  `view` null) or one of its views (fixed at `view`, drawn from the
 *  fisheye's stream or recording). */
export type FisheyeGeometry = { lens: CalibratedLens; view: Pose | null };

type CameraGeometryFields = {
  id: string;
  lens?: Lens | null;
  parent_id?: string | null;
  view?: Pose | null;
};

export function geometryFor(
  cameras: CameraGeometryFields[],
  cameraId: string | null | undefined,
): FisheyeGeometry | null {
  if (!cameraId) return null;
  const cam = cameras.find((c) => c.id === cameraId);
  if (!cam) return null;
  if (cam.parent_id) {
    const parent = cameras.find((c) => c.id === cam.parent_id);
    return parent && isCalibrated(parent.lens) && cam.view ? { lens: parent.lens, view: cam.view } : null;
  }
  return isCalibrated(cam.lens) ? { lens: cam.lens, view: null } : null;
}

/** Fisheye geometry for a camera id, from the shared camera list. */
export function useFisheyeGeometry(cameraId: string | null | undefined): FisheyeGeometry | null {
  const cams = useQuery({
    queryKey: ["cameras", "all"],
    queryFn: () => api.cameras.list(),
    staleTime: 60_000,
  });
  return useMemo(() => geometryFor(cams.data ?? [], cameraId), [cams.data, cameraId]);
}

// What each fisheye player is showing right now, by camera: the view (and
// its frame shape) the operator is looking at, or null for the whole fisheye.
// Saving a clip reads this, so the clip is the picture on screen.
const FOCUS = new Map<string, ClipView | null>();

/** The view on screen for a camera: a ClipView, null for the whole fisheye,
 *  or undefined when no fisheye player for it is open. */
export function fisheyeFocus(cameraId: string): ClipView | null | undefined {
  return FOCUS.get(cameraId);
}

const LAYOUTS: Array<[Layout, string]> = [
  ["single", "Look around"],
  ["quad", "Four views"],
  ["raw", "Fisheye"],
];

/** Draws a fisheye source (live video, or the canvas a recording is decoded
 *  onto) dewarped over the player it sits in. A view is shown at its pose; the
 *  fisheye itself can be looked around, with a choice of layout. With
 *  `cameraId`, what is on screen is published for clip saving. */
export function FisheyeOverlay({
  geometry,
  video,
  canvas,
  cameraId,
  layoutsAt = "right-12 top-2",
}: {
  geometry: FisheyeGeometry;
  video?: HTMLVideoElement | null;
  canvas?: HTMLCanvasElement | null;
  cameraId?: string;
  /** Tailwind position of the layout switcher, clear of the player's own chrome. */
  layoutsAt?: string;
}) {
  const [layout, setLayout] = useState<Layout>("single");
  const [views, setViews] = useState<Pose[]>(() => VIEW_PRESETS[0].views(geometry.lens));
  const [active, setActive] = useState(0);
  const fixed = useMemo(() => (geometry.view ? [geometry.view] : null), [geometry.view]);

  useEffect(() => {
    if (!cameraId) return;
    return () => {
      FOCUS.delete(cameraId);
    };
  }, [cameraId]);
  const publish = (focus: { view: Pose; aspect: number } | null) => {
    if (cameraId) FOCUS.set(cameraId, focus ? { ...focus.view, aspect: focus.aspect } : null);
  };

  if (fixed) {
    return (
      <FisheyeView
        lens={geometry.lens}
        video={video}
        canvas={canvas}
        views={fixed}
        inset={false}
        onFocus={publish}
      />
    );
  }
  return (
    <>
      <FisheyeView
        lens={geometry.lens}
        video={video}
        canvas={canvas}
        views={views}
        active={active}
        layout={layout}
        interactive
        onChange={(v, a) => {
          setViews(v);
          setActive(a);
        }}
        onFocus={publish}
      />
      <div
        className={`absolute ${layoutsAt} z-10 flex overflow-hidden rounded-md bg-black/60 text-xs font-medium text-white backdrop-blur`}
        onClick={(e) => e.stopPropagation()}
      >
        {LAYOUTS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`px-2 py-1 ${layout === id ? "bg-bf-violet/40 text-white" : "text-bf-muted hover:text-white"}`}
            onClick={() => setLayout(id)}
          >
            {label}
          </button>
        ))}
      </div>
    </>
  );
}
